import React from 'react';
import {
  Education,
  EducationDocList,
  BirthCertificateDocs,
  BirthCertificateDocList,
  IDocumentsList,
  EducationSponsorship
} from '../../../../types';
import ChecklistSection from '../../../atoms/checklist-section';
import Finger, { Status } from '../../../atoms/finger';
import { getVisible, getDocStatus } from '../../../../utilities/documents';
import _isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { useIsSectionLocked } from '../../../../hooks/useIsSectionLocked';
import { SectionId } from '../../../molecules/Sidebar/types';

interface IProps {
  infoOnly: boolean;
  id: string;
  amount: string;
  documents: Education;
  financials: EducationSponsorship;
  birthCertificate?: BirthCertificateDocs;
  onResume: (url: string) => void;
  onUploadClick: (sponsorType: string, docType: string | null, docKey: string, infoOnly: boolean) => void;
  warning?: string;
}

const EducationSection = (props: IProps) => {
  let hasDocs = false;
  const docNodes: React.ReactNode[] = [];
  const infoOnly = props.infoOnly;
  const offer = useSelector((state: ApplicationState) => state.student.offer);
  const isSectionLocked = useIsSectionLocked(SectionId.FINANCE_DOC, offer.status);

  const birthCertificate = props.birthCertificate;
  if (birthCertificate && birthCertificate.certificate) {
    const visibleDocs = getVisible(birthCertificate.certificate, infoOnly) as BirthCertificateDocList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: BirthCertificateDocList, index: number) => {
          const { key, item } = doc;

          if (item.status === 'REJECTED') {
            return (
              <Finger
                id={`rejected-birth-certificate-${key}`}
                hideButton={true}
                reason={item.reason}
                key={`rejected-birth-certificate-${key}`}
                status={Status.Rejected}
                label={`Birth Certificate or Proof of Guardianship ${
                  visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''
                }`}
              />
            );
          } else {
            return (
              <Finger
                id={`birth-certificate-${key}`}
                key={`birth-certificate-${key}`}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please confirm' : 'Requires attention'}
                buttonLabel={infoOnly ? 'Please review' : 'How to prove'}
                status={item.isDocumentFixed ? Status.Complete : getDocStatus(item.status, infoOnly, isSectionLocked)}
                label={`Birth Certificate or Proof of Guardianship ${
                  visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''
                }`}
                hideButton={item.status === 'REPLACED'}
                onClick={() => props.onUploadClick('birthCertificate', 'certificate', key, infoOnly)}
              />
            );
          }
        })
      );
    }
  }
  if (birthCertificate && birthCertificate.translation) {
    const visibleDocs = getVisible(birthCertificate.translation, infoOnly) as IDocumentsList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;
          if (item.status === 'REJECTED') {
            return (
              <Finger
                id={`rejected-birth-certificate-translation-document-${key}`}
                hideButton={true}
                reason={item.reason}
                key={`rejected-birth-certificate-translation-document-${key}`}
                status={Status.Rejected}
                label={`Birth Certificate Translation ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                id={`birth-certificate-translation-${key}`}
                key={`birth-certificate-translation-${key}`}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please confirm' : 'Requires attention'}
                buttonLabel={infoOnly ? 'Please review' : 'How to prove'}
                status={getDocStatus(item.status, infoOnly)}
                label={'Birth Certificate Translation'}
                onClick={() => props.onUploadClick('birthCertificate', 'translation', key, infoOnly)}
              />
            );
          }
        })
      );
    }
  }

  const educationSponsor = props.documents;
  if (educationSponsor && !_isEmpty(educationSponsor)) {
    const visibleDocs = getVisible(educationSponsor, infoOnly) as EducationDocList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: EducationDocList, index: number) => {
          const { key, item } = doc;

          if (item.status === 'REJECTED') {
            return (
              <Finger
                id={`rejected-education-sponsor-${key}`}
                hideButton={true}
                reason={item.reason}
                key={`rejected-education-sponsor-${key}`}
                status={Status.Rejected}
                label={props.financials.loanName}
              />
            );
          } else {
            return (
              <Finger
                id={`education-sponsor-${key}`}
                key={`education-sponsor-${key}`}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please confirm' : 'Requires attention'}
                buttonLabel={infoOnly ? 'Please review' : 'How to prove'}
                status={item.isDocumentFixed ? Status.Complete : getDocStatus(item.status, infoOnly)}
                label={props.financials.loanName}
                onClick={() => props.onUploadClick('educationSponsorship', null, key, infoOnly)}
              />
            );
          }
        })
      );
    }
  }

  if (!hasDocs) {
    return null;
  }
  return (
    <ChecklistSection
      id={props.id}
      label={'Education Loan'}
      edit={() => {
        props.onResume('finances/step-1');
      }}
      warning={props.warning}
      amount={props.amount}
    >
      {docNodes}
    </ChecklistSection>
  );
};

export default EducationSection;
